/**
 * Markdown 渲染工具
 */
import { marked } from 'marked';

// 配置 marked
marked.setOptions({
  breaks: true, // 支持 GFM 换行
  gfm: true, // 启用 GitHub Flavored Markdown
});

/**
 * 渲染 Markdown 为 HTML
 */
export function renderMarkdown(content: string): string {
  try {
    return marked.parse(content) as string;
  } catch (error) {
    console.error('Markdown parse error:', error);
    return escapeHtml(content);
  }
}

/**
 * 转义 HTML 特殊字符
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * 处理代码块，添加展开/收起功能
 */
export function processCodeBlocks(container: HTMLElement): void {
  const codeBlocks = container.querySelectorAll('pre');

  codeBlocks.forEach((pre) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'code-block-wrapper collapsed';

    // 检查代码块是否需要折叠
    const isLong = pre.scrollHeight > 200;

    if (isLong) {
      const btn = document.createElement('button');
      btn.className = 'code-expand-btn';
      btn.textContent = '展开';
      btn.type = 'button';

      btn.addEventListener('click', () => {
        const isCollapsed = wrapper.classList.contains('collapsed');
        wrapper.classList.toggle('collapsed');
        btn.textContent = isCollapsed ? '收起' : '展开';
      });

      wrapper.appendChild(pre.cloneNode(true));
      wrapper.appendChild(btn);
      pre.replaceWith(wrapper);
    }
  });
}

/**
 * 处理图片，添加点击预览功能
 */
export function processImages(container: HTMLElement): void {
  const images = container.querySelectorAll('img:not([data-loaded])');

  images.forEach((img) => {
    // 标记为已处理
    img.setAttribute('data-loaded', 'true');

    img.style.cursor = 'pointer';
    img.addEventListener('click', () => {
      // 创建图片预览模态框
      const modal = document.createElement('div');
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        cursor: pointer;
      `;

      const clonedImg = img.cloneNode(true) as HTMLImageElement;
      clonedImg.style.cssText = `
        max-width: 90%;
        max-height: 90%;
        object-fit: contain;
      `;

      modal.appendChild(clonedImg);
      document.body.appendChild(modal);

      modal.addEventListener('click', () => {
        document.body.removeChild(modal);
      });
    });
  });
}

/**
 * 处理消息内容（Markdown + 其他功能）
 */
export function processMessageContent(container: HTMLElement): void {
  processCodeBlocks(container);
  processImages(container);
  processMediaLinks(container);
}

/**
 * 处理媒体链接，自动将图片/视频/文件链接转换为可预览元素
 */
export function processMediaLinks(container: HTMLElement): void {
  // 查找所有链接
  const links = container.querySelectorAll('a[href]:not([data-media-processed])');

  // 媒体链接的正则表达式
  const imagePatterns = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
  const videoPatterns = /\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i;
  const audioPatterns = /\.(mp3|wav|ogg|m4a|flac)(\?.*)?$/i;
  const pdfPatterns = /\.pdf(\?.*)?$/i;

  links.forEach((link) => {
    const anchor = link as HTMLAnchorElement;
    const href = anchor.href;

    // 标记为已处理
    anchor.setAttribute('data-media-processed', 'true');

    let mediaType: 'image' | 'video' | 'audio' | 'pdf' | null = null;

    if (imagePatterns.test(href)) {
      mediaType = 'image';
    } else if (videoPatterns.test(href)) {
      mediaType = 'video';
    } else if (audioPatterns.test(href)) {
      mediaType = 'audio';
    } else if (pdfPatterns.test(href)) {
      mediaType = 'pdf';
    }

    if (mediaType) {
      anchor.classList.add(`media-link-${mediaType}`);
      anchor.setAttribute('data-media-type', mediaType);

      // 为图片添加内联预览
      if (mediaType === 'image') {
        const wrapper = document.createElement('div');
        wrapper.className = 'media-link-wrapper';

        // 创建图片预览
        const img = document.createElement('img');
        img.src = href;
        img.alt = anchor.textContent || '图片';
        img.className = 'media-preview';
        img.loading = 'lazy';

        // 点击图片打开预览模态框
        img.style.cursor = 'pointer';
        img.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          openMediaPreview(href, 'image');
        });

        wrapper.appendChild(img);
        anchor.parentNode?.insertBefore(wrapper, anchor);
        wrapper.appendChild(anchor);
      }

      // 为视频添加预览播放器
      if (mediaType === 'video') {
        const wrapper = document.createElement('div');
        wrapper.className = 'media-link-wrapper';

        const video = document.createElement('video');
        video.src = href;
        video.controls = true;
        video.className = 'media-preview';
        video.preload = 'metadata';

        wrapper.appendChild(video);
        anchor.parentNode?.insertBefore(wrapper, anchor);
        wrapper.appendChild(anchor);
      }

      // 为音频添加播放器
      if (mediaType === 'audio') {
        const wrapper = document.createElement('div');
        wrapper.className = 'media-link-wrapper';

        const audio = document.createElement('audio');
        audio.src = href;
        audio.controls = true;
        audio.className = 'media-preview';

        wrapper.appendChild(audio);
        anchor.parentNode?.insertBefore(wrapper, anchor);
        wrapper.appendChild(anchor);
      }

      // 为 PDF 添加预览按钮
      if (mediaType === 'pdf') {
        anchor.classList.add('pdf-link');
        anchor.innerHTML = `<span class="pdf-icon">📄</span> ${anchor.textContent}`;
        anchor.setAttribute('target', '_blank');
      }
    }
  });
}

/**
 * 打开媒体预览模态框
 */
function openMediaPreview(src: string, type: string): void {
  const modal = document.createElement('div');
  modal.className = 'media-preview-modal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    cursor: pointer;
  `;

  let content: HTMLElement;

  if (type === 'image') {
    content = document.createElement('img');
    (content as HTMLImageElement).src = src;
    content.style.cssText = `
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
      border-radius: 8px;
    `;
  } else if (type === 'video') {
    content = document.createElement('video');
    (content as HTMLVideoElement).src = src;
    (content as HTMLVideoElement).controls = true;
    content.style.cssText = `
      max-width: 90%;
      max-height: 90%;
    `;
  } else {
    content = document.createElement('div');
    content.textContent = '不支持的媒体类型';
  }

  modal.appendChild(content);
  document.body.appendChild(modal);

  // 点击关闭
  modal.addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // ESC 关闭
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      document.body.removeChild(modal);
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);
}
